export const STATUS_TONE_STYLES = {
  success: {
    badgeClass:
      "border border-cfActiveBorder bg-cfActiveBg text-cfActiveText",
    surface: "#ffffff",
    surfaceMuted: "var(--cf-active-bg-color)",
    border: "var(--cf-active-border-color)",
    text: "var(--cf-active-text-color)",
  },
  warning: {
    badgeClass:
      "border border-cfWarningBorder bg-cfWarningBg text-cfWarningText",
    surface: "var(--cf-warning-bg-color)",
    surfaceMuted: "rgba(247, 144, 9, 0.1)",
    border: "var(--cf-warning-border-color)",
    text: "var(--cf-warning-text-color)",
  },
  danger: {
    badgeClass:
      "border border-cfStatusBlockedText bg-cfStatusBGBlocked text-cfStatusBlockedText",
    surface: "var(--cf-status-blocked-bg, #fde2e0)",
    surfaceMuted: "rgba(255, 68, 54, 0.08)",
    border: "var(--cf-status-blocked-text, #ff4436)",
    text: "var(--cf-status-blocked-text, #ff4436)",
  },
  neutral: {
    badgeClass: "border border-gray-300 bg-gray-100 text-gray-600",
    surface: "#f8fafc",
    surfaceMuted: "#f1f5f9",
    border: "#cbd5e1",
    text: "#475569",
  },
};

const includesAny = (value, patterns) =>
  patterns.some((pattern) => value.includes(pattern));

export const normalizeCredentialStatus = (status) => {
  const normalized = (status || "").toString().trim().toLowerCase();

  if (!normalized) return "unknown";

  if (includesAny(normalized, ["revoked"])) return "revoked";
  if (includesAny(normalized, ["expired", "expried"])) return "expired";
  if (
    includesAny(normalized, [
      "not verified",
      "unverified",
      "invalid",
      "failed",
      "unresolved",
    ])
  ) {
    return "not_verified";
  }
  if (includesAny(normalized, ["verified", "valid", "active", "resolved"])) {
    return "verified";
  }

  return "unknown";
};

export const inferCredentialStatus = (details, fallbackStatus) => {
  const topLevelStatus = normalizeCredentialStatus(details?.status);
  if (topLevelStatus !== "unknown") return topLevelStatus;

  if (normalizeCredentialStatus(details?.revocation?.status) === "revoked") {
    return "revoked";
  }

  if (normalizeCredentialStatus(details?.expiry?.status) === "expired") {
    return "expired";
  }

  const signatureStatus = normalizeCredentialStatus(details?.signature?.status);
  const issuerStatus = normalizeCredentialStatus(details?.issuer?.status);

  if (signatureStatus === "not_verified" || issuerStatus === "not_verified") {
    return "not_verified";
  }

  const fallback = normalizeCredentialStatus(fallbackStatus);
  return fallback === "unknown" ? "unknown" : fallback;
};

export const getCredentialStatusTone = (status) => {
  const normalized = normalizeCredentialStatus(status);

  if (normalized === "revoked") return "danger";
  if (normalized === "expired") return "warning";
  if (normalized === "not_verified") return "neutral";
  if (normalized === "verified") return "success";

  return "neutral";
};

export const getVerificationCheckStatus = (section, status) => {
  const raw = (status || "").toString().trim().toLowerCase();

  // Absent or empty status must not default to an optimistic success state
  if (!raw) return "unknown";

  if (section === "blockchain_issuer") {
    if (
      includesAny(raw, [
        "not anchored",
        "not_anchored",
        "not verified",
        "false",
        "no",
        "failed",
        "invalid",
      ])
    ) {
      return "not_anchored";
    }
    if (includesAny(raw, ["anchored", "on chain", "on-chain", "true", "yes", "verified"])) {
      return "anchored";
    }
    return "unknown";
  }

  if (section === "blockchain_vc") {
    const norm = normalizeCredentialStatus(status);
    return norm === "verified" ? "verified" : "invalid";
  }

  if (section === "signature") {
    // Use normalizeCredentialStatus so aliases like "valid" resolve to "verified"
    const norm = normalizeCredentialStatus(status);
    return norm === "verified" ? "verified" : "invalid";
  }

  if (section === "expiry") {
    return raw === "expired" ? "expired" : "active";
  }

  if (section === "revocation") {
    return raw === "revoked" ? "revoked" : "clear";
  }

  if (section === "issuer") {
    return raw === "resolved" ? "resolved" : "unresolved";
  }

  return raw;
};

export const getVerificationCheckTone = (section, status) => {
  const normalized = getVerificationCheckStatus(section, status);

  if (normalized === "unknown") return "neutral";

  if (normalized === "anchored") return "success";
  if (normalized === "not_anchored") return "danger";

  if (
    normalized === "invalid" ||
    normalized === "revoked" ||
    normalized === "unresolved"
  ) {
    return normalized === "unresolved" ? "warning" : "danger";
  }

  if (normalized === "expired") return "warning";
  if (
    normalized === "verified" ||
    normalized === "active" ||
    normalized === "clear" ||
    normalized === "resolved"
  ) {
    return "success";
  }

  return "neutral";
};
